import { useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Button from '@/components/Button';
import { Product } from '@/types/fulfill/inventory';

import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import type { SyntheticEvent } from 'react';

interface CreateKitStepSelectProductsProps {
  step: number;
  setCurrentStep: (step: number) => void;
  catalogProducts: Product[];
  catalogSearch: string;
  isLoadingCatalog: boolean;
  onOpenCatalog: () => void;
  onChangeCatalogInput: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  selectedProducts: Product[];
  onChangeSelectedProducts: (event: SyntheticEvent, newValue: Product[]) => void;
  isDisabled: boolean;
}

const CreateKitStepSelectProducts = (props: CreateKitStepSelectProductsProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Selecciona al menos 2 productos de tu catálogo que formarán este Kit.
      </Typography>

      <Autocomplete
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        filterOptions={(options) => options}
        options={props.catalogProducts}
        value={props.selectedProducts}
        inputValue={props.catalogSearch}
        loading={props.isLoadingCatalog}
        onOpen={props.onOpenCatalog}
        onInputChange={props.onChangeCatalogInput}
        onChange={props.onChangeSelectedProducts}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.sku === value.sku}
        noOptionsText="No se encontraron productos. Intenta con otra búsqueda."
        renderOption={(optionProps, option) => {
          const { key, ...rest } = optionProps;
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <img
                src={option.thumbnail}
                alt={option.name}
                style={{ width: 36, height: 36, borderRadius: '0.5rem', objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {option.name}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                  SKU {option.sku}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              props.selectedProducts.length === 0 ? 'Buscar por SKU o nombre' : undefined
            }
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {props.isLoadingCatalog && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <Typography variant="body2" sx={{ color: theme.palette.grey[500], mt: 0.75 }}>
        Se muestran los primeros 20 resultados. Sigue escribiendo para afinar la búsqueda por SKU o
        nombre.
      </Typography>

      <Grid container justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" fontWeight="bold">
          Productos seleccionados: {props.selectedProducts.length}
        </Typography>
        <Button disabled={props.isDisabled} onClick={() => props.setCurrentStep(props.step + 1)}>
          Continuar
        </Button>
      </Grid>
    </Box>
  );
};

export default CreateKitStepSelectProducts;
